import { z } from 'zod';
import { Currency, NexudusEntity } from './Invoice';

/**
 * Basket item types supported by the portal checkout.
 */
export type BasketItemType = 'booking' | 'event' | 'contract' | 'product' | 'course';

export const basketItemTypeSchema = z.enum([
  'booking',
  'event',
  'contract',
  'product',
  'course',
]) satisfies z.ZodType<BasketItemType>;

/**
 * Booking data as stored in the basket.
 *
 * The raw object comes from the booking form and is enriched by
 * checkoutStore.addToBasket with pricing and product fields.
 */
export interface BasketBookingData {
  Id?: number | null;
  ResourceId: number;
  ResourceName: string;
  FromTime: string;
  ToTime: string;
  FloorPlanDeskId?: number;
  Notes?: string;
  BookingVisitors?: Array<{ FullName: string; Email: string }>;
  IncludeZoomInvite?: boolean;
  Tentative?: boolean;
  DiscountCode?: string;
  SelectedShift?: unknown;

  /** Set by addToBasket from the bookingPrice param */
  BookingPrice?: number;
  /** Set by addToBasket from bookingPrice.Currency.Code */
  BookingPriceCurrencyCode?: string;
  /** Set by addToBasket from bookingPrice error description */
  BookingPriceMessage?: string;
  /** Set by addToBasket from resourceProducts param */
  ResourceProducts?: Array<{
    ProductId: number;
    ProductName?: string;
    Price?: number;
    Quantity: number;
  }>;
  /** Display label for any applied discount */
  discountLabel?: string;

  /** Set by addToBasket */
  inBasket?: boolean;
}

/**
 * Event attendee product selection.
 */
export interface EventProduct {
  Id: number;
  Name: string;
  Price: number;
  PriceCurrencyCode?: string;
  Quantity: number;
  Attendees: Array<{ FullName: string; Email: string }>;
}

/**
 * Event data as stored in the basket.
 */
export interface BasketEventData {
  Id: number;
  Name: string;
  StartDate?: string | null;
  MostExpensivePrice: number;
  EventProducts: EventProduct[];
}

/**
 * Product included with a tariff signup.
 */
export interface SignupProduct {
  Name: string;
  Price: number;
  ProductCurrencyCode?: string;
  Recurrent: boolean;
}

/**
 * Tariff (plan) reference within a contract basket item.
 */
export interface BasketTariff {
  Id: number;
  Name: string;
  Price?: number;
  PriceDecimal?: number;
  Currency?: Currency;
  Description?: string;
  SignupProducts?: SignupProduct[];
}

/**
 * Contract/plan data as stored in the basket.
 */
export interface BasketContractData {
  Id?: number;
  tariff?: BasketTariff;
  startDate?: string | null;
  BookingPrice?: number;
}

/**
 * Product data as stored in the basket.
 *
 * Matches the Nexudus ProductDto shape for fields used in checkout.
 */
export interface BasketProductData extends NexudusEntity {
  Name: string;
  Description?: string;
  Price: number;
  PriceFormatted?: string;
  Currency?: Currency;
  Quantity: number;
  RegularCharge: boolean;
  AlwaysRecurrent?: boolean;
  AlwaysOneOff?: boolean;
  CurrentStock?: number;
  AllowNegativeStock?: boolean;
  TaxRate?: number;
  HasTimePasses?: boolean;
  ProductCurrencyCode?: string;
  Tags?: string;
}

/**
 * Course data as stored in the basket.
 */
export interface BasketCourseData {
  Id: number;
  Title?: string;
  TariffPrice?: number;
  TariffCurrencyCode?: string;
  CanAccessCourse?: boolean;
}

/**
 * Discriminated union of all basket item types.
 *
 * Use the `type` field to narrow to the specific data shape:
 * ```ts
 * if (item.type === 'booking') {
 *   item.data.ResourceName // ✓ typed
 * }
 * ```
 */
export type BasketItem =
  | { type: 'booking'; data: BasketBookingData }
  | { type: 'event'; data: BasketEventData }
  | { type: 'contract'; data: BasketContractData }
  | { type: 'product'; data: BasketProductData }
  | { type: 'course'; data: BasketCourseData };
